/// <reference path="../../base-app/common/scripts/types/custom-types.d.ts" />

/**
 * Applies roving tabindex: every option is removed from the tab order first,
 * then exactly one visible option receives `tabindex="0"`.
 * `aria-selected` reflects the storefront country (set in Liquid) and is
 * not modified here.
 *
 * @param {CountrySwitcherOptionElement[]} allOptions - Every listbox option (`<li role="option">`)
 * @param {CountrySwitcherOptionElement[]} visibleOptions - Options not filtered out
 * @param {number} [focusIndex=0] - Index into `visibleOptions` for `tabindex="0"`
 * @returns {void}
 */
const setRovingTabindex = (allOptions, visibleOptions, focusIndex = 0) => {
  allOptions.forEach((el) => {
    el.setAttribute('tabindex', '-1')
  })

  visibleOptions.forEach((el, index) => {
    el.setAttribute('tabindex', index === focusIndex ? '0' : '-1')
  })
}

/**
 * Resolves which visible option should hold roving focus.
 *
 * @param {CountrySwitcherOptionElement[]} items - Visible listbox options
 * @param {Element | null} [preferredElement=document.activeElement] - Element to prefer if still in `items`
 * @returns {number} Index into `items` for roving focus
 */
const getRovingFocusIndex = (
  items,
  preferredElement = document.activeElement
) => {
  const focusedIndex = items.indexOf(preferredElement)
  if (focusedIndex >= 0) return focusedIndex

  const selectedIndex = items.findIndex(
    (el) => el.getAttribute('aria-selected') === 'true'
  )
  return selectedIndex >= 0 ? selectedIndex : 0
}

/**
 * @param {string} searchText - Normalized lowercase text from `data-search-text`
 * @param {string} query - Raw search input value
 * @returns {boolean} Whether the list item should remain visible
 */
const matchesCountryFilter = (searchText, query) => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  return searchText.includes(normalizedQuery)
}

/**
 * @extends {LocalizationFormElement}
 */
class LocalizationForm extends HTMLElement {
  constructor() {
    super()

    this.button = this.querySelector('[data-component-country-switcher-button]')

    this.closeButtons = Array.from(
      this.querySelectorAll('[data-component-country-switcher-close]')
    )

    this.dialog = this.querySelector('[data-component-country-switcher-dialog]')

    this.select = this.querySelector('[data-component-country-switcher-select]')

    this.selectTrigger = this.querySelector(
      '[data-component-country-switcher-select-trigger]'
    )

    this.selectDropdown = this.querySelector(
      '[data-component-country-switcher-select-dropdown]'
    )

    this.listbox = this.querySelector(
      '[data-component-country-switcher-listbox]'
    )

    this.form = this.querySelector('[data-component-country-switcher-form]')

    this.searchInput = this.querySelector(
      '[data-component-country-switcher-search]'
    )

    this.emptyMessage = this.querySelector(
      '[data-component-country-switcher-empty]'
    )

    this.options = Array.from(
      this.querySelectorAll('[data-component-country-switcher-option]')
    )

    this.handleClick = this.handleClick.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.handleBackdropClick = this.handleBackdropClick.bind(this)
    this.handleRovingKeydown = this.handleRovingKeydown.bind(this)
    this.handleOptionClick = this.handleOptionClick.bind(this)
    this.handleOptionKeydown = this.handleOptionKeydown.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handleSearchKeydown = this.handleSearchKeydown.bind(this)
    this.handleSelectTriggerClick = this.handleSelectTriggerClick.bind(this)
    this.handleSelectTriggerKeydown = this.handleSelectTriggerKeydown.bind(this)
    this.handleSelectDropdownKeydown =
      this.handleSelectDropdownKeydown.bind(this)
  }

  /** @returns {void} */
  connectedCallback() {
    this.button.addEventListener('click', this.handleClick)
    this.dialog.addEventListener('close', this.handleDialogClose)
    this.selectTrigger?.addEventListener('click', this.handleSelectTriggerClick)
    this.options.forEach((option) => {
      option.addEventListener('click', this.handleOptionClick)
      option.addEventListener('keydown', this.handleOptionKeydown)
    })
  }

  /** @returns {void} */
  disconnectedCallback() {
    this.button.removeEventListener('click', this.handleClick)
    this.dialog.removeEventListener('close', this.handleDialogClose)
    this.selectTrigger?.removeEventListener(
      'click',
      this.handleSelectTriggerClick
    )
    this.teardownDialogListeners()
    this.teardownSelectListeners()
    this.options.forEach((option) => {
      option.removeEventListener('click', this.handleOptionClick)
      option.removeEventListener('keydown', this.handleOptionKeydown)
    })
  }

  /**
   * @returns {boolean} Whether the custom select dropdown is expanded
   */
  get isSelectExpanded() {
    return this.selectTrigger?.getAttribute('aria-expanded') === 'true'
  }

  /**
   * @returns {CountrySwitcherOptionElement[]} Options that are not filtered out
   */
  getVisibleOptions() {
    return this.options.filter((option) => !option.hidden)
  }

  /**
   * @param {CountrySwitcherOptionElement[]} visibleOptions
   * @param {number} focusIndex
   * @returns {void}
   */
  focusListboxOption(visibleOptions, focusIndex) {
    const option = visibleOptions[focusIndex]
    if (!option) return

    setRovingTabindex(this.options, visibleOptions, focusIndex)
    option.focus()
  }

  /**
   * Shows or hides list items from the search query and updates roving tabindex.
   *
   * @param {string} query - Search input value
   * @returns {void}
   */
  filterOptions(query) {
    let visibleCount = 0

    this.options.forEach((option) => {
      const searchText = option.dataset.searchText || ''
      const matches = matchesCountryFilter(searchText, query)
      option.hidden = !matches
      option.classList.toggle('u-hidden', !matches)
      if (matches) visibleCount += 1
    })

    if (this.emptyMessage) {
      this.emptyMessage.hidden = visibleCount > 0
      this.emptyMessage.classList.toggle('u-hidden', visibleCount > 0)
    }

    const visibleOptions = this.getVisibleOptions()
    const focusIndex = getRovingFocusIndex(visibleOptions)
    setRovingTabindex(this.options, visibleOptions, focusIndex)
  }

  /** Clears the search field and restores the full option list. @returns {void} */
  resetFilter() {
    if (this.searchInput) {
      this.searchInput.value = ''
    }
    this.filterOptions('')
  }

  /** @returns {void} */
  handleDialogClose() {
    this.button.setAttribute('aria-expanded', 'false')
    this.collapseSelect()
    this.resetFilter()
    this.teardownDialogListeners()
    this.button?.focus()
  }

  /**
   * Light-dismiss fallback when `closedby` is unsupported (e.g. Safari).
   * Clicks on the dialog element itself (backdrop), not its children, close the modal.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleBackdropClick(event) {
    if (event.target !== this.dialog) return
    this.closeDialog()
  }

  /** Removes dialog-scoped listeners. @returns {void} */
  teardownDialogListeners() {
    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.handleClose)
    })
    this.dialog.removeEventListener('click', this.handleBackdropClick)
  }

  /** Removes select dropdown scoped listeners. @returns {void} */
  teardownSelectListeners() {
    this.selectTrigger?.removeEventListener(
      'keydown',
      this.handleSelectTriggerKeydown
    )
    this.selectDropdown?.removeEventListener(
      'keydown',
      this.handleSelectDropdownKeydown
    )
    this.listbox?.removeEventListener('keydown', this.handleRovingKeydown)
    this.searchInput?.removeEventListener('input', this.handleSearch)
    this.searchInput?.removeEventListener('keydown', this.handleSearchKeydown)
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const isExpanded = this.button.getAttribute('aria-expanded') === 'true'

    if (isExpanded) {
      this.closeDialog()
    } else {
      this.openDialog()
    }
  }

  /** @returns {void} */
  openDialog() {
    this.dialog.showModal()
    this.button.setAttribute('aria-expanded', 'true')
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.handleClose)
    })

    if (!('closedBy' in this.dialog)) {
      this.dialog.addEventListener('click', this.handleBackdropClick)
    }

    this.collapseSelect()
    this.resetFilter()

    requestAnimationFrame(() => {
      this.selectTrigger?.focus()
    })
  }

  /** @returns {void} */
  closeDialog() {
    if (!this.dialog.open) return
    this.dialog.close()
    this.button.focus()
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleClose(event) {
    event.preventDefault()
    event.stopPropagation()
    this.closeDialog()
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleSelectTriggerClick(event) {
    event.preventDefault()
    event.stopPropagation()
    this.toggleSelect()
  }

  /** @returns {void} */
  toggleSelect() {
    if (this.isSelectExpanded) {
      this.collapseSelect()
    } else {
      this.expandSelect()
    }
  }

  /** @returns {void} */
  expandSelect() {
    if (!this.selectTrigger || !this.selectDropdown) return

    this.selectTrigger.setAttribute('aria-expanded', 'true')
    this.selectDropdown.classList.remove('u-hidden')

    this.selectTrigger.addEventListener(
      'keydown',
      this.handleSelectTriggerKeydown
    )
    this.selectDropdown.addEventListener(
      'keydown',
      this.handleSelectDropdownKeydown
    )
    this.listbox?.addEventListener('keydown', this.handleRovingKeydown)
    this.searchInput?.addEventListener('input', this.handleSearch)
    this.searchInput?.addEventListener('keydown', this.handleSearchKeydown)

    this.resetFilter()

    requestAnimationFrame(() => {
      this.searchInput?.focus()
    })
  }

  /** @returns {void} */
  collapseSelect() {
    this.selectTrigger?.setAttribute('aria-expanded', 'false')
    this.selectDropdown?.classList.add('u-hidden')
    this.teardownSelectListeners()
    this.selectTrigger?.focus()
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleSelectTriggerKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== ' ')
      return

    event.preventDefault()
    this.expandSelect()
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleSelectDropdownKeydown(event) {
    if (event.key !== 'Escape') return

    event.preventDefault()
    event.stopPropagation()
    this.collapseSelect()
  }

  /** @returns {void} */
  handleSearch() {
    this.filterOptions(this.searchInput?.value || '')
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleSearchKeydown(event) {
    if (event.key !== 'ArrowDown') return

    const visibleOptions = this.getVisibleOptions()
    if (visibleOptions.length === 0) return

    event.preventDefault()
    const focusIndex = getRovingFocusIndex(visibleOptions)
    this.focusListboxOption(visibleOptions, focusIndex)
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleRovingKeydown(event) {
    const visibleOptions = this.getVisibleOptions()
    const currentIndex = visibleOptions.indexOf(document.activeElement)
    if (currentIndex === -1) return

    let nextIndex = currentIndex

    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % visibleOptions.length
        break
      case 'ArrowUp':
        if (currentIndex === 0) {
          event.preventDefault()
          this.searchInput?.focus()
          return
        }
        nextIndex = currentIndex - 1
        break
      default:
        return
    }

    event.preventDefault()
    this.focusListboxOption(visibleOptions, nextIndex)
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleOptionKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    this.selectCountry(event.currentTarget)
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleOptionClick(event) {
    event.preventDefault()
    this.selectCountry(event.currentTarget)
  }

  /**
   * @param {CountrySwitcherOptionElement} option - Clicked or activated listbox option
   * @returns {void}
   */
  selectCountry(option) {
    const value = option.getAttribute('data-value')
    const input = this.form?.querySelector('[name="country_code"]')

    if (!value || !input) return

    input.value = value
    this.form.submit()
  }
}

customElements.define('localization-form', LocalizationForm)
